import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface Rider {
  id: string
  name: string
  email: string
  phone: string
  profileImage?: string
  isVerified: boolean
  status: 'pending' | 'active' | 'suspended'
  rating: number
  totalDeliveries: number
  currentBalance: number
  vehicleType: string
  vehicleNumber: string
  licenseNumber: string
  createdAt: string
}

interface AuthState {
  rider: Rider | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

interface AuthActions {
  login: (email: string, password: string) => Promise<boolean>
  register: (data: RegisterData) => Promise<boolean>
  logout: () => void
  updateProfile: (data: Partial<Rider>) => Promise<boolean>
  clearError: () => void
  setLoading: (loading: boolean) => void
}

interface RegisterData {
  name: string
  email: string
  phone: string
  password: string
  vehicleType: string
  vehicleNumber: string
  licenseNumber: string
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      rider: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        
        try {
          const response = await fetch(`${API_BASE_URL}/auth/riders/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          })

          const data = await response.json()

          if (data.success) {
            set({
              rider: data.rider,
              token: data.token,
              isAuthenticated: true,
              isLoading: false,
            })
            return true
          } else {
            set({ error: data.message || 'Login failed', isLoading: false })
            return false
          }
        } catch (error) {
          set({ error: 'Network error. Please try again.', isLoading: false })
          return false
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null })
        
        try {
          const response = await fetch(`${API_BASE_URL}/auth/riders/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          })

          const result = await response.json()

          if (result.success) {
            set({ isLoading: false })
            return true
          } else {
            set({ error: result.message || 'Registration failed', isLoading: false })
            return false
          }
        } catch (error) {
          set({ error: 'Network error. Please try again.', isLoading: false })
          return false
        }
      },

      logout: () => {
        set({
          rider: null,
          token: null,
          isAuthenticated: false,
          error: null,
        })
      },

      updateProfile: async (data: Partial<Rider>) => {
        const { token } = get()
        if (!token) return false

        set({ isLoading: true, error: null })
        
        try {
          const response = await fetch(`${API_BASE_URL}/riders/profile`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
          })

          const result = await response.json()

          if (result.success) {
            set((state) => ({
              rider: state.rider ? { ...state.rider, ...data } : null,
              isLoading: false,
            }))
            return true
          } else {
            set({ error: result.message || 'Update failed', isLoading: false })
            return false
          }
        } catch (error) {
          set({ error: 'Network error. Please try again.', isLoading: false })
          return false
        }
      },

      clearError: () => set({ error: null }),
      setLoading: (loading: boolean) => set({ isLoading: loading }),
    }),
    {
      name: 'rider-auth-storage',
      partialize: (state) => ({
        rider: state.rider,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)